import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import { CheckCircle2, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { useClearCspData, useUploadCspData } from '@/api/csp';
import { fmtDate, normalizeRow, useCspRows } from '@/lib/csp';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * CSP → Upload. The Partner Center "Microsoft Tenant Report" CSV is parsed in the browser and
 * REPLACES the shared dataset for everyone. Column aliases live in `normalizeRow` (lib/csp.ts).
 */
const EXPECTED = ['Customer Name', 'Offer Name', 'Subscription Auto-Renew Date', 'Current Subscription Quantity', 'Commitment Term', 'Auto RenewFlag', 'Subscription Start Date'];

export default function CspUploadPage() {
  const { dataset } = useCspRows();
  const upload = useUploadCspData();
  const clear = useClearCspData();
  const input = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState<{ fields: string[]; rows: Record<string, string>[]; file: string; count: number } | null>(null);

  function parse(file: File) {
    if (!/\.csv$/i.test(file.name)) { toast.error('Choose a .csv file'); return; }
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const subscriptions = results.data.map(normalizeRow).filter((r) => r.customer);
        if (!subscriptions.length) { toast.error('No subscription rows found — is this the Microsoft Tenant Report?'); return; }
        setPreview({ fields: results.meta.fields ?? [], rows: results.data.slice(0, 8), file: file.name, count: subscriptions.length });
        try {
          await upload.mutateAsync({ subscriptions, fileName: file.name });
          toast.success(`${subscriptions.length} subscriptions saved from ${file.name} — shared with everyone`);
        } catch (e) {
          toast.error(`Upload failed: ${e instanceof Error ? e.message : 'unknown error'}`);
        }
      },
      error: (err) => toast.error(`Could not read the CSV: ${err.message}`),
    });
  }

  async function onClear() {
    if (!window.confirm('Delete the shared dataset? Everyone will see an empty CSP dashboard until a new report is uploaded.')) return;
    await clear.mutateAsync();
    setPreview(null);
    toast.success('Dataset cleared');
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-5 md:px-6">
      <PageHeader title="Upload report" subtitle="Import the Partner Center Microsoft Tenant Report (CSV)" />

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div
            role="button" tabIndex={0}
            onClick={() => input.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter') input.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) parse(f); }}
            className={cn('flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors',
              drag ? 'border-primary bg-primary/5' : 'border-border hover:border-ring hover:bg-muted/30')}>
            {upload.isPending ? <Loader2 className="h-9 w-9 animate-spin text-muted-foreground" /> : <UploadCloud className="h-9 w-9 text-muted-foreground" />}
            <div className="mt-3 text-sm font-medium text-foreground">Drop the CSV here or click to browse</div>
            <div className="mt-1 text-xs text-muted-foreground"><strong>Replaces</strong> the current dataset for everyone</div>
            <input ref={input} type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) parse(f); e.target.value = ''; }} />
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            <div className="mb-1.5 font-medium text-foreground">Columns used</div>
            <div className="flex flex-wrap gap-1.5">{EXPECTED.map((c) => <code key={c} className="rounded bg-muted px-1.5 py-0.5">{c}</code>)}</div>
            <p className="mt-2">Common aliases (Customer, Plan, End Date, Quantity, Auto Renew…) are recognised too. "Customer Name (Account Manager)" is split into both.</p>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5 text-sm shadow-sm">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Current dataset</h2>
          {dataset ? (
            <>
              <div className="mt-2 flex items-center gap-2 text-foreground"><CheckCircle2 className="h-4 w-4 text-success" /> {dataset.subscriptions.length} subscriptions</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {dataset.fileName && <>{dataset.fileName}<br /></>}Uploaded {fmtDate(dataset.uploadedAt)} by {dataset.uploadedBy}
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" asChild><Link to={ROUTES.csp.dashboard}>Go to dashboard</Link></Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={onClear} disabled={clear.isPending}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Clear
                </Button>
              </div>
            </>
          ) : <p className="mt-2 text-xs text-muted-foreground">Nothing uploaded yet.</p>}
        </section>
      </div>

      {preview && (
        <section className="mt-4 rounded-lg border border-border bg-card shadow-sm">
          <div className="border-b border-divider px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Preview · {preview.file} · first {preview.rows.length} of {preview.count} rows
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>{preview.fields.map((f) => <th key={f} className="whitespace-nowrap px-3 py-2 font-medium">{f}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {preview.rows.map((r, i) => <tr key={i}>{preview.fields.map((f) => <td key={f} className="whitespace-nowrap px-3 py-1.5">{r[f]}</td>)}</tr>)}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
